import { c as _c } from "react/compiler-runtime";
import { IcoSettings16 } from '@onefootprint/icons';
import type { GraphOperationUpdateSettings, PlaybookSettings as PlaybookSettingsType } from '@onefootprint/request-types/dashboard';
import { Dialog, IconButton } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useConfirmClose from '@/components/playbook-builder/hooks/use-confirm-close';
import Modernize from './modernize';
import Settings from './settings/settings';
export type PlaybookSettingsProps = {
  onSubmit?: (data: Omit<GraphOperationUpdateSettings, 'kind'>) => Promise<void>;
  playbook: PlaybookSettingsType;
};
const PlaybookSettings = t0 => {
  const $ = _c(43);
  const {
    onSubmit,
    playbook
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "settingsDialog"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const [isOpen, setIsOpen] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const canEditPlaybook = !!onSubmit;
  let t2;
  if ($[1] !== onSubmit) {
    t2 = {
      mutationFn: onSubmit
    };
    $[1] = onSubmit;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const submitMutation = useMutation(t2);
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setIsOpen(false);
    t4 = () => setIsFormDirty(false);
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] !== isFormDirty || $[6] !== submitMutation.isPending) {
    t5 = {
      isDirty: isFormDirty,
      isMutationPending: submitMutation.isPending,
      onCloseParent: t3,
      onClearDirty: t4
    };
    $[5] = isFormDirty;
    $[6] = submitMutation.isPending;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const {
    attemptClose
  } = useConfirmClose(t5);
  let t6;
  if ($[8] !== submitMutation) {
    t6 = async data => {
      await submitMutation.mutateAsync(data);
      setIsOpen(false);
    };
    $[8] = submitMutation;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const handleSubmit = t6;
  let t7;
  if ($[10] !== t) {
    t7 = t(_temp);
    $[10] = t;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setIsOpen(true);
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t7) {
    t9 = <IconButton icon={IcoSettings16} aria-label={t7} onClick={t8} size="compact" variant="outline" />;
    $[13] = t7;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t) {
    t10 = t(_temp2);
    $[15] = t;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== canEditPlaybook || $[18] !== submitMutation.isPending || $[19] !== t) {
    t11 = canEditPlaybook ? {
      form: "settings-form",
      label: t(_temp3),
      loading: submitMutation.isPending,
      type: "submit"
    } : undefined;
    $[17] = canEditPlaybook;
    $[18] = submitMutation.isPending;
    $[19] = t;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== attemptClose || $[22] !== canEditPlaybook || $[23] !== submitMutation.isPending || $[24] !== t) {
    t12 = canEditPlaybook ? {
      disabled: submitMutation.isPending,
      label: t(_temp4),
      onClick: attemptClose.handler
    } : undefined;
    $[21] = attemptClose;
    $[22] = canEditPlaybook;
    $[23] = submitMutation.isPending;
    $[24] = t;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const t13 = canEditPlaybook ? handleSubmit : undefined;
  const t14 = canEditPlaybook && !playbook.isModern;
  let t15;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => setIsOpen(false);
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== t14) {
    t16 = <Modernize canMigrate={t14} onModernize={t15} />;
    $[27] = t14;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== playbook || $[30] !== t13 || $[31] !== t16) {
    t17 = <Settings playbook={playbook} onSubmit={t13} setIsFormDirty={setIsFormDirty} modernize={t16} />;
    $[29] = playbook;
    $[30] = t13;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== attemptClose || $[34] !== isOpen || $[35] !== t10 || $[36] !== t11 || $[37] !== t12 || $[38] !== t17) {
    t18 = <Dialog title={t10} open={isOpen} onClose={attemptClose} isConfirmation={true} size="compact" primaryButton={t11} secondaryButton={t12}>{t17}</Dialog>;
    $[33] = attemptClose;
    $[34] = isOpen;
    $[35] = t10;
    $[36] = t11;
    $[37] = t12;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t18 || $[41] !== t9) {
    t19 = <>{t9}{t18}</>;
    $[40] = t18;
    $[41] = t9;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  return t19;
};
export default PlaybookSettings;
function _temp($) {
  return $.trigger;
}
function _temp2($_0) {
  return $_0.title;
}
function _temp3($_1) {
  return $_1.actions.save;
}
function _temp4($_2) {
  return $_2.actions.cancel;
}